/* istanbul ignore file */

import _, { difference, each, get } from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import odata from '@bingads-webui-component-legacy/http/channel/odata';
import * as locationTargetConverter from '@bingads-webui-campaign-legacy/location-targets/util/locationTargetConverter';
import { DeviceKeys } from '../target-constants';

export function getCountriesLocations(locationIds, lcid, cid, aid) {
  if (_.size(locationIds) === 0) {
    return Promise.resolve([]);
  }
  const reqs = _.map(locationIds, id => ({
    type: 'GET',
    url: `${edmCampaign.Customers.$withKey(cid).Accounts.$withKey(aid).Locations.$withKey(id).path}?%24expand=ParentLocations`,
    headers: lcid ? {
      lcid,
    } : undefined,
  }));

  return odata.batch({ reqs, needContentType: true });
}

const targetTypes = {
  Device: 'Device',
  Age: 'Age',
  Gender: 'Gender',
  Location: 'Location',
  Audience: 'Audience',
};

export function getTargetConstraints(deal, targetType) {
  const ret = {
    mandatory: [],
    isRestricted: deal.Type === 'Restricted',
  };

  if (!deal || !deal.DealTargetConstraints) { return ret; }

  const devicesConstraints = deal.DealTargetConstraints.find(target => target.TargetType === targetType);

  if (!devicesConstraints) {
    return ret;
  }

  ret.mandatory = devicesConstraints.Mandatory || [];
  ret.isRestricted = devicesConstraints.DealTargetRestrictionLevel === 'Restricted';

  return ret;
}

async function getLocationCritetions({
  serverContext, countries,
}) {
  const cid = serverContext.CurrentCustomer.Id;
  const aid = serverContext.CurrentAccount.Id;
  const lcid = serverContext ? serverContext.CurrentUser.LocaleID : '1033';

  const response = await getCountriesLocations(countries, lcid, cid, aid);
  const locationCriterions = [];

  each(response, (location) => {
    const criterion = locationTargetConverter.buildLocationCriterionFromSearchResult(
      location.data,
      { mode: 'location', status: 'Included' }
    );
    criterion.location = get(criterion, 'Criterion.LocationCriterion.NameTypeCombo');
    locationCriterions.push(criterion);
  });

  return locationCriterions;
}

export async function modelConstraints(deal, adGroup, serverContext) {
  if (!deal || !adGroup) { return adGroup; }

  const isCreating = !adGroup.Id;

  const deviceConstraints = getTargetConstraints(deal, targetTypes.Device);
  const locationConstraints = getTargetConstraints(deal, targetTypes.Location);
  const ageConstraints = getTargetConstraints(deal, targetTypes.Age);
  const genderConstraints = getTargetConstraints(deal, targetTypes.Gender);
  const audienceConstraints = getTargetConstraints(deal, targetTypes.Audience);

  const dealLocationTargets = adGroup.locationTargets;
  if (isCreating && locationConstraints && locationConstraints.isRestricted === false && dealLocationTargets && dealLocationTargets.currentLevelSettings) {
    dealLocationTargets.currentLevelSettings.targetOption = 'everywhere';
    dealLocationTargets.currentLevelSettings.currentLocations = [];
  } else if (get(adGroup, ['locationTargets', 'currentLevelSettings', 'initLocations'], []).length === 0) {
    // locations
    const locations = await getLocationCritetions({
      serverContext,
      countries: locationConstraints.mandatory,
    });

    if (locations.length !== 0) {
      dealLocationTargets.currentLevelSettings.currentLocations = locations;
      dealLocationTargets.currentLevelSettings.initLocations = [];
      dealLocationTargets.currentLevelSettings.targetOption = 'specific';
      dealLocationTargets.useParentLevel = false;
    }
  }
  const adGroupModel = {};
  if (ageConstraints.isRestricted) {
    adGroupModel.ageRanges = [];
    adGroupModel.initialAgeRanges = [];
    adGroupModel.negativeAgeRanges = [];
  }

  if (genderConstraints.isRestricted) {
    adGroupModel.initialGenderTypes = [];
    adGroupModel.negativeGenderTYpes = [];
    adGroupModel.genderTypes = [];
  }

  if (audienceConstraints.isRestricted) {
    adGroupModel.audiences = [];
  }

  return {
    ...adGroup,
    ...adGroupModel,
    deal,
    locationTargets: dealLocationTargets,
    dealLocationList: locationConstraints.mandatory,
    deviceTypes: isCreating ? deviceConstraints.mandatory : adGroup.deviceTypes,
    deviceRestrictions: deviceConstraints.isRestricted ? Object.keys(DeviceKeys) : difference(Object.keys(DeviceKeys), deviceConstraints.mandatory),
    deviceRestricted: deviceConstraints.isRestricted,
    locationRestricted: locationConstraints.isRestricted,
    // locationRestricted: false,
    ageRestricted: ageConstraints.isRestricted,
    genderRestricted: genderConstraints.isRestricted,
    audienceRestricted: audienceConstraints.isRestricted,
    genreRestricted: true,
  };
}
